import { useList, useShow, useUpdate, useDelete } from '@refinedev/core'
import { Show } from '@refinedev/mui'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const CATEGORY_OPTIONS = ['tone', 'terminology', 'fact', 'preference', 'correction']
const STATUS_OPTIONS = ['active', 'superseded', 'archived']

const truncate = (value: string, limit = 160) => {
  if (!value) return ''
  return value.length > limit ? `${value.slice(0, limit)}...` : value
}

const MemoriesShow: React.FC = () => {
  const navigate = useNavigate()
  const { query } = useShow({ resource: 'memories' })
  const record = query.data?.data

  const [content, setContent] = useState('')
  const [category, setCategory] = useState('correction')
  const [status, setStatus] = useState('active')
  const [confidence, setConfidence] = useState('0.5')

  const { mutate: updateMemory, isLoading: isUpdating } = useUpdate()
  const { mutate: archiveMemory, isLoading: isArchiving } = useDelete()
  const { mutate: deleteMemory, isLoading: isDeleting } = useDelete()

  const { data: activityData } = useList({
    resource: 'memory-activity',
    filters: record
      ? [{ field: 'memory_point_id', operator: 'eq', value: record.id }]
      : [],
    pagination: { current: 1, pageSize: 25 },
    queryOptions: {
      enabled: Boolean(record?.id),
    },
  })

  const { data: relatedData } = useList({
    resource: 'memories',
    filters: record
      ? [
          { field: 'org_uuid', operator: 'eq', value: record.org_uuid },
          { field: 'memory_key', operator: 'eq', value: record.memory_key },
        ]
      : [],
    pagination: { current: 1, pageSize: 20 },
    queryOptions: {
      enabled: Boolean(record?.org_uuid && record?.memory_key),
    },
  })

  useEffect(() => {
    if (!record) {
      return
    }
    setContent(record.content || '')
    setCategory(record.category || 'correction')
    setStatus(record.status || 'active')
    setConfidence(String(record.confidence ?? 0.5))
  }, [record])

  const relatedMemories = (relatedData?.data || []).filter(
    (item: any) => item.id !== record?.id
  )

  const save = () => {
    if (!record || record.id == null) return

    updateMemory({
      resource: 'memories',
      id: record.id,
      values: {
        content,
        category,
        status,
        confidence: Number(confidence),
      },
    })
  }

  const archive = () => {
    if (!record || record.id == null) return
    archiveMemory({ resource: 'memories', id: record.id })
  }

  const remove = () => {
    if (!record || record.id == null) return

    const confirmed = window.confirm('Delete this memory permanently?')
    if (!confirmed) return

    deleteMemory(
      {
        resource: 'memories',
        id: record.id,
        meta: { hardDelete: true },
      },
      {
        onSuccess: () => navigate('/memories'),
      }
    )
  }

  return (
    <Show isLoading={query.isLoading}>
      {record && (
        <Stack spacing={2}>
          <Typography variant="h6">Memory Detail</Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={`Org: ${record.org_name || record.org_uuid}`} />
            <Chip label={`Scope: ${record.scope}`} />
            {record.need_id ? (
              <Chip label={`Need: ${record.need_name || `#${record.need_id}`}`} />
            ) : null}
            <Chip label={`Source: ${record.source_type}`} variant="outlined" />
          </Stack>

          <TextField
            label="Content"
            multiline
            minRows={4}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            fullWidth
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Confidence"
              value={confidence}
              onChange={(event) => setConfidence(event.target.value)}
              sx={{ minWidth: 120 }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={save} disabled={isUpdating}>
              Save
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={archive}
              disabled={isArchiving || status === 'archived'}
            >
              Archive
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={remove}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Stack>

          <Divider />
          <Typography variant="h6">Activity History</Typography>
          {activityData?.data?.length ? (
            <Stack spacing={1}>
              {activityData.data.map((item: any) => (
                <Box key={item.id} sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.created_at ? new Date(item.created_at).toLocaleString() : ''} - {item.action}
                  </Typography>
                  <Typography variant="body2">{truncate(item.reasoning || '', 220)}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No activity logged for this memory yet.</Alert>
          )}

          <Divider />
          <Typography variant="h6">Related Memories</Typography>
          {relatedMemories.length ? (
            <Stack spacing={1}>
              {relatedMemories.map((item: any) => (
                <Box key={item.id} sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.id} • v{item.version} • {item.status}
                  </Typography>
                  <Typography variant="body2">{truncate(item.content || '')}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No related memories found.</Alert>
          )}
        </Stack>
      )}
    </Show>
  )
}

export default MemoriesShow
